import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { ScaledLine3D } from 'features/assetViz/objects/decor/ScaledLine3D'
import { useSurfaceLines } from 'features/assetViz/objects/surfaces/util/useSurfaceLines'
import { MarkupPrimitiveData } from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import {
  GeckoComponentPath,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
} from 'types'

import { useFindSelectedPrimitiveXyzs } from './useFindSelectedPrimitiveXyzs'
import { useMarkupLayerProperties } from './useMarkupLayerProperties'

/**
 * A component used to standardize the handling (and appearance) of primitives
 * with outlines (such as rects, lines, polygons, etc.)
 * @param markupLayer         The markup layer the primitives belong to
 * @param primitives          The primitives whose outlines are being rendered
 * @param paths               The paths of the outlines (in component coordinates)
 * @constructor
 */
export const PrimitiveOutline3D: React.FC<{
  markupLayer: GeckoMarkupLayer
  primitives: GeckoMarkupPrimitive[]
  paths: GeckoComponentPath[]
}> = ({ markupLayer, primitives, paths }) => {
  //----------------------------------------------------------------------------

  const { clipConfig } = useAssetVizInternal()

  const lines = useSurfaceLines(paths)

  const { color, opacity, isEditingThisLayer, isSelectable } =
    useMarkupLayerProperties({ markupLayer, primitive: undefined })

  const primitiveXyzs = useFindSelectedPrimitiveXyzs(
    primitives,
    lines?.indexes,
    lines?.xyzs
  )

  const selectedXyzs = isEditingThisLayer ? primitiveXyzs : []

  const layers =
    isEditingThisLayer || isSelectable
      ? RenderLayerID.VisibleCollidable
      : RenderLayerID.VisibleNonCollidable

  const userData = React.useMemo<MarkupPrimitiveData | undefined>(
    () =>
      (isEditingThisLayer || isSelectable) && lines !== undefined
        ? {
            markupPrimitives: {
              primitives: primitives,
              indexes: lines.indexes,
            },
          }
        : undefined,
    [isEditingThisLayer, isSelectable, primitives, lines]
  )

  //----------------------------------------------------------------------------

  return lines === undefined || lines.xyzs.length === 0 ? null : (
    <>
      <ScaledLine3D
        points={lines.xyzs}
        color={color}
        opacity={opacity}
        linewidth={3}
        dashed={false}
        dashScale={5}
        layers={layers}
        userData={userData}
        clippingPlanes={clipConfig.planes}
      />
      {selectedXyzs.length === 0 ? null : (
        <ScaledLine3D
          points={selectedXyzs}
          color={color}
          opacity={opacity}
          linewidth={6}
          dashed={false}
          dashScale={5}
          layers={RenderLayerID.VisibleCollidable}
          clippingPlanes={clipConfig.planes}
        />
      )}
    </>
  )
}
